import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Flex } from "@redotech/arbiter/flex";
import { RedoIcon } from "@redotech/arbiter/icon/redo-icon";
import { Link } from "@redotech/arbiter/link";
import { RedoSpinner } from "@redotech/arbiter/loading/redo-spinner";
import { RedoNotification } from "@redotech/arbiter/notification/redo-notification";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import CheckCircleFilledIcon from "@redotech/redo-icons/check-circle_filled.svg";
import XCircleFilledIcon from "@redotech/redo-icons/x-circle_filled.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { getReturnHref } from "@redotech/redo-return-core-common/merchant-record-url";
import { memo, useContext, useEffect } from "react";
import {
  ProcessingRunsContext,
  type ProcessingProgress,
  type TrackedProcessingRun,
} from "./processing-runs-provider";

/** A finished run goes quietly; a stopped one stays until closed. */
const AUTO_DISMISS_MS = 3000;
const ICON_SIZE = 20;

/** One card per return this session sent to processing, outliving the return page. */
export const ProcessingRunToasts = memo(function ProcessingRunToasts() {
  const processingRuns = useContext(ProcessingRunsContext);
  // A card waits for its first poll, so it never shows a return it knows nothing about.
  const runs =
    processingRuns?.runs.filter((run) => run.progress && !run.dismissed) ?? [];
  if (!processingRuns || runs.length === 0) {
    return null;
  }
  return (
    <Flex
      bottom="3xl"
      dir="column"
      gap="xl"
      position="fixed"
      right="3xl"
      w="sm"
      zIndex="overlay"
    >
      {runs.map((run) => (
        <ProcessingRunToast
          dismiss={processingRuns.dismiss}
          key={run.returnId}
          run={run}
        />
      ))}
    </Flex>
  );
});

const ProcessingRunToast = memo(function ProcessingRunToast({
  run,
  dismiss,
}: {
  run: TrackedProcessingRun;
  dismiss(returnId: string): void;
}) {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);
  const { progress } = run;
  const state = progress?.state;

  useEffect(() => {
    if (state !== "done") {
      return;
    }
    const timeout = setTimeout(() => dismiss(run.returnId), AUTO_DISMISS_MS);
    return () => clearTimeout(timeout);
  }, [state, dismiss, run.returnId]);

  if (!progress) {
    return null;
  }
  const target = progress.orderName
    ? t("order {{orderName}}", { orderName: progress.orderName })
    : t("this return");
  const href = getReturnHref(routes.returnsBaseUrl, {
    _id: run.returnId,
    type: progress.type,
  });

  return (
    <RedoNotification
      breakpoint="mobile"
      dataTestId={`processing-run-toast-${run.returnId}`}
      dismiss={() => dismiss(run.returnId)}
      hideCloseX
      noPadding
    >
      {() => (
        <Flex align="flex-start" gap="lg" p="xl">
          <Flex pt="xxs" shrink={0}>
            <StateIcon progress={progress} />
          </Flex>
          <Flex dir="column" gap="xs" grow={1}>
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              {stateTitle(progress, t)}
            </Text>
            <Text fontSize="sm" textColor="secondary">
              {state === "done" &&
                t("Return for {{target}} was processed successfully.", {
                  target,
                })}
              {state === "failed" && (
                <>
                  {t("Return for {{target}} couldn't be processed.", {
                    target,
                  })}{" "}
                  <Link to={href}>{t("View return")}</Link>
                </>
              )}
              {state !== "done" &&
                state !== "failed" &&
                t(
                  "Processing return for {{target}}. You may leave this page.",
                  { target },
                )}
            </Text>
          </Flex>
          <RedoButton
            aria-label={t("Dismiss")}
            hierarchy="tertiary"
            IconLeading={XCloseIcon}
            onClick={() => dismiss(run.returnId)}
            size="sm"
          />
        </Flex>
      )}
    </RedoNotification>
  );
});

function stateTitle(
  progress: ProcessingProgress,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  switch (progress.state) {
    case "done":
      return t("Return processed");
    case "failed":
      return t("Couldn't process return");
    case "running":
    case "waiting":
      return t("Processing return");
  }
}

const StateIcon = memo(function StateIcon({
  progress,
}: {
  progress: ProcessingProgress;
}) {
  switch (progress.state) {
    case "done":
      return (
        <RedoIcon
          color="success"
          Icon={CheckCircleFilledIcon}
          size={ICON_SIZE}
        />
      );
    case "failed":
      return (
        <RedoIcon color="error" Icon={XCircleFilledIcon} size={ICON_SIZE} />
      );
    case "running":
    case "waiting":
      return <RedoSpinner size="xs" />;
  }
});
